import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Loader2, Plus, Trash2, Edit2, Home, CheckCircle, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';

export const Addresses = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [loadingCep, setLoadingCep] = useState(false);
  const [cepFound, setCepFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addressForm, setAddressForm] = useState({
    name: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    zipcode: '',
    phone: ''
  });

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    fetchAddresses();
  }, [user]);

  const fetchAddresses = async () => {
    try {
      const response = await axios.get(`${API}/addresses`, { withCredentials: true });
      setAddresses(response.data);
    } catch (error) {
      console.error('Error fetching addresses:', error);
    } finally {
      setLoading(false);
    }
  };

  // Format CEP: 00000-000
  const formatCep = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 8);
    if (numbers.length <= 5) return numbers;
    return `${numbers.slice(0, 5)}-${numbers.slice(5)}`;
  };

  // Format phone: (00) 00000-0000
  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 11);
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
  };

  // Fetch address from ViaCEP API
  const fetchAddressFromCep = async (cep) => {
    const cleanCep = cep.replace(/\D/g, '');
    if (cleanCep.length !== 8) {
      setCepFound(false);
      return;
    }
    
    setLoadingCep(true);
    setCepFound(false);
    
    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cleanCep}/json/`);
      
      if (response.data.erro) {
        toast.error('CEP não encontrado');
        setCepFound(false);
      } else {
        setAddressForm(prev => ({
          ...prev,
          street: response.data.logradouro || '',
          neighborhood: response.data.bairro || '',
          city: response.data.localidade || '',
          state: response.data.uf || ''
        }));
        setCepFound(true);
        toast.success('Endereço encontrado!');
      }
    } catch (error) {
      console.error('Error fetching CEP:', error);
      toast.error('Erro ao buscar CEP');
      setCepFound(false);
    } finally {
      setLoadingCep(false);
    }
  };

  const handleCepChange = (e) => {
    const formatted = formatCep(e.target.value);
    setAddressForm({ ...addressForm, zipcode: formatted });
    
    if (formatted.replace(/\D/g, '').length === 8) {
      fetchAddressFromCep(formatted);
    } else {
      setCepFound(false);
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setAddressForm({ ...addressForm, phone: formatted });
  };

  const resetForm = () => {
    setAddressForm({
      name: '', street: '', number: '', complement: '',
      neighborhood: '', city: '', state: '', zipcode: '', phone: ''
    });
    setShowForm(false);
    setEditingId(null);
    setCepFound(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validations
    if (addressForm.name.length < 3) {
      toast.error('Nome deve ter pelo menos 3 caracteres');
      return;
    }
    if (addressForm.zipcode.replace(/\D/g, '').length !== 8) {
      toast.error('CEP inválido');
      return;
    }
    if (addressForm.phone.replace(/\D/g, '').length < 10) {
      toast.error('Telefone inválido');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/addresses/${editingId}`, addressForm, { withCredentials: true });
        toast.success('Endereço atualizado!');
      } else {
        await axios.post(`${API}/addresses`, addressForm, { withCredentials: true });
        toast.success('Endereço adicionado!');
      }
      await fetchAddresses();
      resetForm();
    } catch (error) {
      toast.error('Erro ao salvar endereço');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (address) => {
    setAddressForm({
      name: address.name,
      street: address.street,
      number: address.number,
      complement: address.complement || '',
      neighborhood: address.neighborhood,
      city: address.city,
      state: address.state,
      zipcode: address.zipcode,
      phone: address.phone
    });
    setEditingId(address.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await axios.delete(`${API}/addresses/${id}`, { withCredentials: true });
      setAddresses(addresses.filter(a => a.id !== id));
      toast.success('Endereço removido!');
    } catch (error) {
      toast.error('Erro ao remover endereço');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-[#0f172a] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Carregando endereços...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0f172a] pt-24 pb-12">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 py-12 mb-8">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <MapPin className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-white">Meus Endereços</h1>
                <p className="text-emerald-100">{addresses.length} {addresses.length === 1 ? 'endereço cadastrado' : 'endereços cadastrados'}</p>
              </div>
            </div>
            {!showForm && (
              <Button 
                onClick={() => setShowForm(true)}
                className="bg-white text-emerald-600 hover:bg-emerald-50"
              >
                <Plus className="h-4 w-4 mr-2" />
                Novo Endereço
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4">
        {/* Add/Edit Form */}
        {showForm && (
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 p-6 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                {editingId ? 'Editar Endereço' : 'Novo Endereço'}
              </h2>
              <button onClick={resetForm} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-xl">
                <X className="h-5 w-5 text-slate-500" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Nome do Endereço *</Label>
                  <Input 
                    value={addressForm.name} 
                    onChange={(e) => setAddressForm({...addressForm, name: e.target.value})} 
                    placeholder="Ex: Casa, Trabalho"
                    required 
                  />
                </div>
                <div>
                  <Label>CEP *</Label>
                  <div className="relative">
                    <Input 
                      value={addressForm.zipcode} 
                      onChange={handleCepChange}
                      placeholder="00000-000"
                      maxLength={9}
                      required 
                      className={cepFound ? 'border-green-500 pr-10' : ''}
                    />
                    {loadingCep && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-emerald-500" />
                    )}
                    {cepFound && !loadingCep && (
                      <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                    )}
                  </div>
                </div>
                <div>
                  <Label>Rua *</Label>
                  <Input 
                    value={addressForm.street} 
                    onChange={(e) => setAddressForm({...addressForm, street: e.target.value})} 
                    placeholder="Ex: Rua das Flores"
                    required 
                  />
                </div>
                <div>
                  <Label>Número *</Label>
                  <Input 
                    value={addressForm.number} 
                    onChange={(e) => setAddressForm({...addressForm, number: e.target.value.replace(/\D/g, '')})} 
                    placeholder="Ex: 123"
                    required 
                  />
                </div>
                <div>
                  <Label>Complemento</Label>
                  <Input 
                    value={addressForm.complement} 
                    onChange={(e) => setAddressForm({...addressForm, complement: e.target.value})} 
                    placeholder="Ex: Apto 101"
                  />
                </div>
                <div>
                  <Label>Bairro *</Label>
                  <Input 
                    value={addressForm.neighborhood} 
                    onChange={(e) => setAddressForm({...addressForm, neighborhood: e.target.value})} 
                    placeholder="Ex: Centro"
                    required 
                  />
                </div>
                <div>
                  <Label>Cidade *</Label>
                  <Input 
                    value={addressForm.city} 
                    onChange={(e) => setAddressForm({...addressForm, city: e.target.value})} 
                    placeholder="Ex: São Paulo"
                    required 
                  />
                </div>
                <div>
                  <Label>Estado *</Label>
                  <Input 
                    value={addressForm.state} 
                    onChange={(e) => setAddressForm({...addressForm, state: e.target.value.toUpperCase().slice(0, 2)})} 
                    placeholder="Ex: SP"
                    maxLength={2}
                    required 
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Telefone *</Label>
                  <Input 
                    value={addressForm.phone} 
                    onChange={handlePhoneChange}
                    placeholder="(00) 00000-0000"
                    required 
                  />
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button 
                  type="submit" 
                  disabled={saving}
                  className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  {editingId ? 'Atualizar' : 'Salvar'} Endereço
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Addresses List */}
        {addresses.length > 0 ? (
          <div className="grid md:grid-cols-2 gap-6">
            {addresses.map((address, index) => (
              <div 
                key={address.id}
                className="group bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 p-6 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center">
                      <Home className="h-6 w-6 text-emerald-500" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-slate-900 dark:text-white">{address.name}</h3>
                      <span className="text-xs text-slate-500 dark:text-slate-400">Endereço #{index + 1}</span>
                    </div>
                  </div>
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleEdit(address)}
                      className="p-2 text-slate-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(address.id)}
                      disabled={deletingId === address.id}
                      className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    >
                      {deletingId === address.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2 text-sm">
                  <p className="text-slate-700 dark:text-slate-300">
                    {address.street}, {address.number}
                    {address.complement && ` - ${address.complement}`}
                  </p>
                  <p className="text-slate-600 dark:text-slate-400">
                    {address.neighborhood}, {address.city} - {address.state}
                  </p>
                  <p className="text-slate-600 dark:text-slate-400">
                    CEP: {address.zipcode}
                  </p>
                  <p className="text-slate-600 dark:text-slate-400">
                    Tel: {address.phone}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : !showForm && (
          /* Empty State */
          <div className="max-w-lg mx-auto text-center py-16">
            <div className="w-32 h-32 bg-gradient-to-br from-emerald-100 to-teal-100 dark:from-emerald-900/30 dark:to-teal-900/30 rounded-full flex items-center justify-center mx-auto mb-8">
              <MapPin className="h-16 w-16 text-emerald-400" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">
              Nenhum endereço cadastrado
            </h2>
            <p className="text-slate-600 dark:text-slate-400 mb-8">
              Adicione um endereço para facilitar suas compras e entregas!
            </p>
            <Button 
              onClick={() => setShowForm(true)}
              size="lg"
              className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 rounded-full px-8"
            >
              <Plus className="h-5 w-5 mr-2" />
              Adicionar Endereço
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
